import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Investment } from '../types/Investment';
import {
  getInvestments, updateInvestment, deleteInvestment,
  contributeToInvestment, saveParsedInvestment, ParsedInvestment,
} from '../services/api';
import { monthlyToAnnualRate, fmtRate as fmtPct } from '../utils/investmentRate';
import EntityQuickEntry from './EntityQuickEntry';
import './Investments.css';
import { mensagemDeErro } from '../utils/mensagemDeErro';
import Toast from './Toast';
/* O MESMO número que o schema do servidor usa — ver utils/limites.ts. */
import { LIMITES } from '../utils/limites';
import { formatPct, brlOrMasked } from '../utils/format';
import { useConfirmar } from '../contexts/Confirmacao';
import { useFechavelComVoltar } from '../contexts/VoltarDoCelular';

const PROJECTION_HORIZONS = [
  { label: '1 ano',   months: 12  },
  { label: '2 anos',  months: 24  },
  { label: '5 anos',  months: 60  },
  { label: '10 anos', months: 120 },
];

/**
 * A projeção da carteira inteira, não só da parte que rende.
 *
 * Antes esta função filtrava fora as aplicações sem taxa e devolvia o total
 * só delas — enquanto a coluna "Crescimento" da tabela dividia esse total pelo
 * `totalInvested` de TODAS. Com uma aplicação sem taxa na carteira, o "Total
 * acumulado" aparecia menor que o investido e o percentual saía negativo.
 * Aplicação sem taxa agora entra e fica parada, que é o que ela de fato faz.
 */
function projectionRows(investments: Investment[], totalMonths: number) {
  const principal = investments.reduce((s, inv) => s + inv.amount, 0);
  // Key checkpoints depending on horizon
  const checkpoints =
    totalMonths <= 12  ? Array.from({ length: totalMonths }, (_, i) => i + 1) :
    totalMonths <= 24  ? [1,2,3,4,5,6,9,12,15,18,21,24].filter(m => m <= totalMonths) :
    totalMonths <= 60  ? [3,6,12,24,36,48,60].filter(m => m <= totalMonths) :
                         [6,12,24,36,60,84,120].filter(m => m <= totalMonths);

  return checkpoints.map(n => {
    const total = investments.reduce(
      (s, inv) => s + inv.amount * Math.pow(1 + Math.max(inv.monthlyRate, 0) / 100, n),
      0,
    );
    return { months: n, total, earnings: total - principal };
  });
}

const INVESTMENT_TYPES = [
  'CDB', 'Tesouro Direto', 'LCI/LCA', 'Ações', 'FII', 'ETF', 'Criptomoedas', 'Poupança', 'Outro',
];

interface FormState {
  name: string;
  type: string;
  amount: string;
  monthlyRate: string;
}

const EMPTY_FORM: FormState = { name: '', type: 'CDB', amount: '', monthlyRate: '' };

interface InvestmentsProps {
  /** O olhinho de "Ocultar valores" do cabeçalho. Ver `utils/format.ts`. */
  hideValues?: boolean;

  onInvestmentsChange?: () => void;
  /**
   * Um aporte cria transação, então a lista de lançamentos e a projeção
   * precisam recarregar junto — do mesmo jeito que em Metas (item 27).
   */
  onTransactionRecorded?: () => void;
  /**
   * Recarrega quando a carteira muda FORA desta tela — aportar pelo campo de
   * lançamento, ou debitar de um investimento no formulário.
   *
   * Sem isto, esta seção só carregava na montagem: o aporte gravava, os totais
   * do topo mudavam, e os cards aqui continuavam com o valor velho até um F5.
   */
  refreshTrigger?: number;
}

const Investments: React.FC<InvestmentsProps> = ({
  onInvestmentsChange, onTransactionRecorded, refreshTrigger, hideValues,
}) => {
  const confirmar = useConfirmar();
  const [investments, setInvestments] = useState<Investment[]>([]);
  /* O erro do servidor vai para o toast; ver utils/mensagemDeErro.ts. */
  const [erroToast, setErroToast] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormState>(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  /** Sempre fechada ao abrir a tela. Ver a projeção é uma decisão dela. */
  const [showProjection, setShowProjection] = useState(false);
  const [horizonIdx, setHorizonIdx] = useState(0);
  /** Qual card está com o campo de aporte aberto, e o valor digitado nele. */
  const [aporteId, setAporteId] = useState<string | null>(null);
  const [aporteValor, setAporteValor] = useState('');
  const [aporteBusy, setAporteBusy] = useState(false);
  /**
   * De onde veio o dinheiro do aporte — e é a pergunta que decide TUDO.
   *
   * `'conta'`  → saiu do caixa dela. Vira transação de débito e o saldo cai.
   *              O patrimônio NÃO cai: o dinheiro continua sendo dela, só
   *              mudou de lugar. É o `POST /:id/contribute`.
   *
   * `'aplicado'` → o dinheiro já estava lá e ela só está anotando: rendimento
   *              do mês, ou um valor que estava errado. Nada sai do caixa, e
   *              não existe transação. É o `PUT /:id`, que o código já chamava
   *              de "correção".
   *
   * Os dois caminhos já existiam e estavam documentados no backend; o que
   * faltava era perguntar. Sem a pergunta, anotar um rendimento pelo botão
   * Aportar derrubava o caixa dela pelo valor do rendimento — dinheiro saindo
   * que nunca saiu.
   */
  const [aporteOrigem, setAporteOrigem] = useState<'conta' | 'aplicado'>('conta');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setInvestments(await getInvestments());
    } catch (err) {
      console.error('Erro ao carregar investimentos:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Pula a primeira execução: `load()` acima já cobre a montagem, e recarregar
  // duas vezes na abertura só gastaria uma requisição.
  const montado = useRef(false);
  useEffect(() => {
    if (!montado.current) { montado.current = true; return; }
    load();
  }, [refreshTrigger, load]);


  /* Delega para `brlOrMasked`: é o que faz o olhinho de "Ocultar valores"
     alcançar esta seção. Antes era um `Intl.NumberFormat` próprio, e como não
     havia um lugar por onde todo dinheiro passasse, o olhinho só chegava nos
     quatro cartões do topo. */
  const fmt = (v: number) => brlOrMasked(v, !!hideValues);

  const openEdit = (inv: Investment) => {
    setEditingId(inv.id);
    setFormData({
      name: inv.name,
      type: inv.type,
      amount: String(inv.amount),
      monthlyRate: inv.monthlyRate > 0 ? String(inv.monthlyRate) : '',
    });
    setFormError('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData(EMPTY_FORM);
    setFormError('');
  };

  /*
   * O voltar do celular fecha ESTE formulário antes de perguntar se quer sair.
   *
   * O relato foi exato: "quando estou com um formulário pra preencher, com o
   * teclado em evidência, e clico em voltar, queria que o form fechasse".
   * Só os modais e os menus estavam na pilha — os formulários que vivem DENTRO
   * da seção não, então voltar deles caía direto na pergunta de sair.
   */
  useFechavelComVoltar(showForm, closeForm);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) { setFormError('Nome é obrigatório.'); return; }
    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0) { setFormError('Valor investido inválido.'); return; }
    const monthlyRate = formData.monthlyRate !== '' ? parseFloat(formData.monthlyRate) : 0;
    if (isNaN(monthlyRate) || monthlyRate < 0) { setFormError('Taxa mensal inválida.'); return; }

    if (!editingId) return; // criar é sempre pelo campo único

    setSaving(true);
    try {
      // Correção, não aporte: `updateInvestment` NÃO cria transação. Mudar
      // 5.000 para 5.500 aqui é "rendeu" ou "corrigi o número"; para dinheiro
      // que saiu da conta, o caminho é o botão Aportar.
      await updateInvestment(editingId, {
        name: formData.name.trim(), type: formData.type, amount, monthlyRate,
      });
      await load();
      closeForm();
      onInvestmentsChange?.();
    } catch (e) {
      setFormError(mensagemDeErro(e, 'Não consegui salvar este investimento.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (inv: Investment) => {
    if (!await confirmar({
      titulo: 'Excluir esta aplicação?',
      alvo: inv.name,
      texto: 'Ela sai da carteira e da projeção de patrimônio. Os aportes já lançados continuam nas transações.',
      acao: 'Excluir', perigo: true,
    })) return;
    try {
      await deleteInvestment(inv.id);
      await load();
      onInvestmentsChange?.();
    } catch (e) {
      setErroToast(mensagemDeErro(e, 'Não consegui excluir o investimento.'));
    }
  };

  /**
   * Aportar REGISTRA a movimentação — era o item 1 de "Em aberto".
   *
   * Existiam dois caminhos para a mesma ação e só um deixava rastro: pelo
   * formulário de transação com "debitar de", o dinheiro saía do caixa; aqui,
   * o valor subia do nada. `Investments.tsx` nunca importou `createTransaction`.
   *
   * Continua sendo duas operações separadas de propósito — aportar cria
   * transação, editar o valor não — porque subir de 5.000 para 5.500 pode ser
   * aporte, rendimento ou correção, e criar transação nos três faria o caixa
   * despencar a cada rendimento anotado.
   */
  const fecharAporte = () => {
    setAporteId(null);
    setAporteValor('');
    setAporteOrigem('conta');
  };

  /* O painel de aporte também: ele tem campo e teclado, igual ao formulário. */
  useFechavelComVoltar(aporteId !== null, fecharAporte);

  const handleAporte = async (inv: Investment) => {
    const val = parseFloat(aporteValor.replace(',', '.'));
    if (isNaN(val) || val <= 0) return;
    setAporteBusy(true);
    try {
      if (aporteOrigem === 'conta') {
        await contributeToInvestment(inv.id, val);
      } else {
        /* Só o número sobe. Sem transação e sem mexer no caixa — ver a nota
           em `aporteOrigem`. */
        await updateInvestment(inv.id, { amount: inv.amount + val });
      }
      await load();
      fecharAporte();
      onInvestmentsChange?.();
      /* Só avisa a lista de lançamentos quando de fato criou um. */
      if (aporteOrigem === 'conta') onTransactionRecorded?.();
    } catch (e) {
      setErroToast(mensagemDeErro(e, 'Não consegui registrar o aporte.'));
    } finally {
      setAporteBusy(false);
    }
  };

  /** O campo único grava por aqui — aporte ou aplicação nova, o backend decide. */
  const handleQuickEntry = async (parsed: ParsedInvestment) => {
    await saveParsedInvestment(parsed);
    await load();
    onInvestmentsChange?.();
    if (parsed.movesCash) onTransactionRecorded?.();
  };

  const totalInvested    = investments.reduce((s, i) => s + i.amount, 0);
  const totalMonthly     = investments.reduce((s, i) => s + i.amount * (i.monthlyRate / 100), 0);
  const hasRates         = investments.some(i => i.monthlyRate > 0);
  const investsWithRates = investments.filter(i => i.monthlyRate > 0);

  const projRows = useMemo(
    () => projectionRows(investments, PROJECTION_HORIZONS[horizonIdx].months),
    [investments, horizonIdx]
  );

  const fmtMonths = (n: number) => {
    if (n < 12) return `${n} ${n === 1 ? 'mês' : 'meses'}`;
    const y = n / 12;
    return `${y} ${y === 1 ? 'ano' : 'anos'}`;
  };

  return (
    <div className="inv-container">
      <Toast message={erroToast} onDismiss={() => setErroToast(null)} />
      <div className="inv-header">
        <div className="inv-title-group">
          <h2>Investimentos</h2>
          {investments.length > 0 && (
            <span className="inv-count-badge">{investments.length}</span>
          )}
        </div>
      </div>

      {investments.length > 0 && (
        <>
          {/*
            Duas caixas pequenas, no estilo dos três números da conta da casa.
            Antes era um bloco verde de largura cheia com o botão de projeção
            ocupando uma linha inteira: tomava mais tela do que a lista de
            aplicações que vem logo abaixo, e ela pediu algo mais sutil.
          */}
          <div className="inv-summary-row" role="group" aria-label="Resumo dos investimentos">
            <div className="inv-summary-item">
              <span className="inv-summary-label">Total investido</span>
              <span className="inv-summary-value total">{fmt(totalInvested)}</span>
            </div>
            {hasRates && (
              <div className="inv-summary-item rende">
                <span className="inv-summary-label">Rende por mês</span>
                <span className="inv-summary-value monthly">{fmt(totalMonthly)}</span>
              </div>
            )}
          </div>
          {hasRates && (
            <button
              className={`inv-btn-projection ${showProjection ? 'active' : ''}`}
              onClick={() => setShowProjection(p => !p)}
              aria-expanded={showProjection}
            >
              {showProjection ? '▲ Ocultar projeção' : '▼ Ver projeção'}
            </button>
          )}
        </>
      )}

      {/* Sem botão "+ Novo investimento": criar é sempre pelo campo único.
          O formulário existe só para editar uma aplicação que já existe —
          mesma decisão já tomada em Metas e em Lembretes. */}
      {!editingId && (
        <EntityQuickEntry modo="investment" onCreate={handleQuickEntry} />
      )}

      {showForm && editingId && (
        <form className="inv-form" onSubmit={handleSave}>
          <h3>Editar investimento</h3>
          <p className="inv-form-nota">
            Mudar o valor aqui é <strong>correção</strong>: rendimento anotado ou
            erro de digitação. Não gera lançamento e não mexe no seu caixa. Para
            dinheiro que saiu da conta, use <strong>Aportar</strong> no card.
          </p>
          <div className="inv-form-grid">
            <div className="inv-form-group inv-col-span-2">
              <label>Nome *</label>
              <input
                type="text"
                maxLength={LIMITES.nome}
                value={formData.name}
                onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                placeholder="Ex: CDB Nubank, Tesouro IPCA 2029..."
                autoFocus
              />
            </div>
            <div className="inv-form-group">
              <label>Tipo *</label>
              <select
                value={formData.type}
                onChange={e => setFormData(p => ({ ...p, type: e.target.value }))}
              >
                {INVESTMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="inv-form-group">
              <label>Valor investido (R$) *</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.amount}
                onChange={e => setFormData(p => ({ ...p, amount: e.target.value }))}
                placeholder="0,00"
              />
            </div>
            <div className="inv-form-group">
              <label>Taxa de rendimento mensal (%) <span style={{ fontSize: '0.8em', color: '#9ca3af', fontWeight: 400 }}>(opcional)</span></label>
              <input
                type="number"
                min="0"
                step="0.0001"
                value={formData.monthlyRate}
                onChange={e => setFormData(p => ({ ...p, monthlyRate: e.target.value }))}
                placeholder="Ex: 0.95"
              />
            </div>
            {formData.amount && formData.monthlyRate && (
              <div className="inv-form-group inv-form-preview">
                <span className="inv-preview-label">
                  Rendimento mensal previsto
                  {/* A taxa é guardada ao mês, mas ninguém anuncia CDB assim.
                      A equivalência anual é o que ela reconhece. */}
                  <em className="inv-preview-annual">
                    {' '}· ≈ {fmtPct(monthlyToAnnualRate(parseFloat(formData.monthlyRate || '0')))} ao ano
                  </em>
                </span>
                <span className="inv-preview-value">
                  {fmt(parseFloat(formData.amount || '0') * (parseFloat(formData.monthlyRate || '0') / 100))}
                </span>
              </div>
            )}
          </div>
          {formError && <p className="inv-form-error">{formError}</p>}
          <div className="inv-form-actions">
            <button type="button" className="inv-btn-cancel" onClick={closeForm}>Cancelar</button>
            <button type="submit" className="inv-btn-save" disabled={saving} aria-busy={saving}>
              {editingId ? 'Atualizar' : 'Adicionar'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="loading-wrap">
          <div className="loading-ring" />
          <span className="loading-text">Carregando investimentos...</span>
        </div>
      ) : investments.length === 0 ? (
        <div className="empty-preview-wrap">
          <span className="empty-preview-badge">Exemplo</span>
          {/*
            O exemplo mostra a seção INTEIRA, com o resumo em cima da lista.
            Antes só vinham as duas aplicações, e a conta vazia não via as
            caixas de total e rendimento — a parte da tela que mais muda de
            cara quando ela começa a usar. Os números batem com a lista de
            baixo: 5.000 + 3.000, e 47,50 + 26,10.
          */}
          <div className="empty-preview-ghost" aria-hidden="true">
            <div className="inv-summary-row">
              <div className="inv-summary-item">
                <span className="inv-summary-label">Total investido</span>
                <span className="inv-summary-value total">{fmt(8000)}</span>
              </div>
              <div className="inv-summary-item rende">
                <span className="inv-summary-label">Rende por mês</span>
                <span className="inv-summary-value monthly">{fmt(73.6)}</span>
              </div>
            </div>
            <span className="inv-btn-projection">▼ Ver projeção</span>
          <div className="inv-list">
            {[
              { type: 'CDB', name: 'CDB Nubank', rate: '0.95', amount: 5000, monthly: 47.5 },
              { type: 'Tesouro Direto', name: 'Tesouro IPCA 2029', rate: '0.87', amount: 3000, monthly: 26.1 },
            ].map((inv, i) => (
              <div key={i} className="inv-item">
                <div className="inv-item-type-badge">{inv.type}</div>
                <div className="inv-item-body">
                  <span className="inv-item-name">{inv.name}</span>
                  <span className="inv-item-rate">{inv.rate}% / mês</span>
                </div>
                <div className="inv-item-amounts">
                  <div className="inv-item-invested">
                    <span className="inv-item-amt-label">Investido</span>
                    <span className="inv-item-amt-value">{fmt(inv.amount)}</span>
                  </div>
                  <div className="inv-item-return">
                    <span className="inv-item-amt-label">Rende / mês</span>
                    <span className="inv-item-amt-value green">+{fmt(inv.monthly)}</span>
                  </div>
                </div>
                <div className="inv-item-actions">
                  <button className="inv-btn-delete" tabIndex={-1}>✕</button>
                </div>
              </div>
            ))}
          </div>
          </div>
          <div className="empty-cta">
            <p className="empty-cta-title">Nenhum investimento cadastrado</p>
            <p className="empty-cta-sub">
              Escreva no campo acima, por exemplo <em>“investi 5 mil em CDB no Nubank que rende 6% ao ano”</em>,
              para acompanhar o rendimento e projetar o crescimento.
            </p>
          </div>
        </div>
      ) : (
        <div className="inv-list">
          {investments.map(inv => {
            const monthly = inv.amount * (inv.monthlyRate / 100);
            return (
              /* O painel de aporte é IRMÃO do card, não filho: dentro dele
                 herdaria o `onClick` que abre a edição, e cada toque no campo
                 de valor abriria o formulário por baixo. */
              <React.Fragment key={inv.id}>
              <div className="inv-item" onClick={() => openEdit(inv)}>
                <div className="inv-item-type-badge">{inv.type}</div>
                <div className="inv-item-body">
                  <span className="inv-item-name">{inv.name}</span>
                  {inv.monthlyRate > 0 && (
                    <span className="inv-item-rate">{fmtPct(inv.monthlyRate)} / mês</span>
                  )}
                </div>
                <div className="inv-item-amounts">
                  <div className="inv-item-invested">
                    <span className="inv-item-amt-label">Investido</span>
                    <span className="inv-item-amt-value">{fmt(inv.amount)}</span>
                  </div>
                  {inv.monthlyRate > 0 && (
                    <div className="inv-item-return">
                      <span className="inv-item-amt-label">Rende / mês</span>
                      <span className="inv-item-amt-value green">+{fmt(monthly)}</span>
                    </div>
                  )}
                </div>
                {/*
                  Com o aporte aberto, o ✕ de EXCLUIR some.

                  Antes os dois ficavam lado a lado, iguais: um fechava o campo
                  e o outro apagava a aplicação. Dois ✕ idênticos a três
                  milímetros um do outro, e o erro entre eles não tem desfazer.
                */}
                <div className="inv-item-actions" onClick={e => e.stopPropagation()}>
                  {aporteId === inv.id ? (
                    <button className="inv-aporte-x" disabled={aporteBusy} onClick={fecharAporte} title="Fechar">✕</button>
                  ) : (
                    <>
                      <button
                        className="inv-btn-aporte"
                        onClick={() => { setAporteId(inv.id); setAporteValor(''); setAporteOrigem('conta'); }}
                      >+ Aportar</button>
                      <button className="inv-btn-delete" onClick={() => handleDelete(inv)} title="Excluir">✕</button>
                    </>
                  )}
                </div>
              </div>

              {aporteId === inv.id && (
                <div className="inv-aporte" onClick={e => e.stopPropagation()}>
                  <label className="inv-aporte-label" htmlFor={`aporte-${inv.id}`}>Quanto entrou</label>
                  <input
                    id={`aporte-${inv.id}`}
                    className="inv-aporte-valor"
                    type="number"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    placeholder="0,00"
                    value={aporteValor}
                    autoFocus
                    disabled={aporteBusy}
                    onChange={e => setAporteValor(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !aporteBusy) handleAporte(inv);
                      if (e.key === 'Escape') fecharAporte();
                    }}
                  />

                  {/*
                    A pergunta que decide o resto.

                    Sem ela, anotar o rendimento do mês pelo botão Aportar
                    derrubava o caixa pelo valor do rendimento — dinheiro
                    saindo que nunca saiu. Cada opção diz o EFEITO, não o nome
                    técnico da operação.
                  */}
                  <div className="inv-aporte-origem">
                    <button
                      type="button"
                      className={`inv-aporte-op${aporteOrigem === 'conta' ? ' ativa' : ''}`}
                      onClick={() => setAporteOrigem('conta')}
                      disabled={aporteBusy}
                    >
                      <strong>Saiu da minha conta</strong>
                      <span>Vira lançamento e o saldo cai. O patrimônio não muda, o dinheiro só trocou de lugar.</span>
                    </button>
                    <button
                      type="button"
                      className={`inv-aporte-op${aporteOrigem === 'aplicado' ? ' ativa' : ''}`}
                      onClick={() => setAporteOrigem('aplicado')}
                      disabled={aporteBusy}
                    >
                      <strong>Já estava aplicado</strong>
                      <span>Rendimento ou correção. Nada sai do saldo e nenhum lançamento é criado.</span>
                    </button>
                  </div>

                  <div className="inv-aporte-acoes">
                    <button className="inv-aporte-cancelar" onClick={fecharAporte} disabled={aporteBusy}>
                      Cancelar
                    </button>
                    <button
                      className="inv-aporte-ok"
                      onClick={() => handleAporte(inv)}
                      disabled={aporteBusy || !aporteValor.trim()}
                      aria-busy={aporteBusy}
                    >
                      Registrar
                    </button>
                  </div>
                </div>
              )}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {showProjection && investments.length > 0 && (
        <div className="inv-projection">
          <div className="inv-proj-header">
            <span className="inv-proj-title">Projeção de crescimento (juros compostos)</span>
            <div className="inv-proj-horizons">
              {PROJECTION_HORIZONS.map((h, i) => (
                <button
                  key={h.label}
                  className={`inv-proj-pill ${horizonIdx === i ? 'active' : ''}`}
                  onClick={() => setHorizonIdx(i)}
                >{h.label}</button>
              ))}
            </div>
          </div>

          <div className="inv-proj-table-wrap">
            <table className="inv-proj-table">
              <thead>
                <tr>
                  <th>Período</th>
                  <th>Total acumulado</th>
                  <th>Rendimento</th>
                  <th>Crescimento</th>
                </tr>
              </thead>
              <tbody>
                {projRows.map(row => {
                  const pct = totalInvested > 0 ? ((row.total / totalInvested - 1) * 100) : 0;
                  const isYear = row.months % 12 === 0;
                  return (
                    <tr key={row.months} className={isYear ? 'proj-row-year' : ''}>
                      <td className="proj-col-period">{fmtMonths(row.months)}</td>
                      <td className="proj-col-total">{fmt(row.total)}</td>
                      <td className="proj-col-earnings">+{fmt(row.earnings)}</td>
                      <td className="proj-col-pct">+{formatPct(pct)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {investsWithRates.length > 1 && (
            <div className="inv-proj-breakdown">
              <span className="inv-proj-breakdown-title">Detalhamento por investimento em {PROJECTION_HORIZONS[horizonIdx].label}</span>
              <div className="inv-proj-cards">
                {investsWithRates.map(inv => {
                  const n = PROJECTION_HORIZONS[horizonIdx].months;
                  const finalVal = inv.amount * Math.pow(1 + inv.monthlyRate / 100, n);
                  const earned = finalVal - inv.amount;
                  return (
                    <div key={inv.id} className="inv-proj-card">
                      <div className="inv-proj-card-top">
                        <span className="inv-proj-card-name">{inv.name}</span>
                        <span className="inv-proj-card-type">{inv.type}</span>
                      </div>
                      <div className="inv-proj-card-row">
                        <span>Investido</span>
                        <span>{fmt(inv.amount)}</span>
                      </div>
                      <div className="inv-proj-card-row green">
                        <span>Total em {PROJECTION_HORIZONS[horizonIdx].label}</span>
                        <span>{fmt(finalVal)}</span>
                      </div>
                      <div className="inv-proj-card-row muted">
                        <span>Rendimento</span>
                        <span>+{fmt(earned)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Investments;
